import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFieldArray, useForm } from "react-hook-form";
import { traceError } from "utils/traceContext";

import { FeeAlert } from "components/facility/FeeAlert";
import { Form } from "components/core/Form";
import {
	TextField,
	EmailField,
	PhoneField,
	SelectField
} from "components/core/FormFields";
import { Button } from "components/core/Button";
import { H4, Paragraph } from "components/core/Typography";
import { Link } from "components/core/Link";
import { AdditionalNameInputsGroup } from "components/facility/UnitReservationForm/AdditionalNameInputsGroup";
import { AdditionalNames } from "components/facility/UnitReservationForm/AdditionalNames";
import { Calendar, WarningIcon } from "components/core/Icons";

import {
	ADDITIONAL_NAMES_FIELD_ARRAY_NAME,
	ADDITIONAL_NAME_INPUTS,
	UNIT_FORM_INPUTS
} from "components/facility/UnitReservationForm/UnitReservationFormInputs";

import formatDatepickerOptions from "utils/formatDatepicker";

import "./UnitReservationForm.scss";
import { trackCTAClick } from "hooks/tracking/utils/clickTrackingUtils";

const UnitReservationForm = ({
	onSubmit,
	reservationWindow,
	unitPrice,
	closures,
	fees,
	totalFees,
	showFeeWarning,
	cityUrl,
	errorReservationMessages,
	setErrorReservationMessages,
	handleFetchNumber
}) => {
	const useFormMethods = useForm({
		mode: "all",
		shouldUnregister: true,
		shouldUseNativeValidation: false,
		defaultValues: {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			moveInDate: ""
		}
	});

	// we have to pass the whole methods object to the form,
	// but we also need to get control out for the field array
	const {
		control,
		getValues,
		watch,
		formState: { isSubmitted }
	} = useFormMethods;
	const [lastSubmitValues, setLastSubmitValues] = useState({});
	const [wasSubmittedWithoutChanges, setIsSubmitWithoutChanges] =
		useState(false);
	const [phone, setPhone] = useState({});

	const { fields, append, remove } = useFieldArray({
		control,
		name: ADDITIONAL_NAMES_FIELD_ARRAY_NAME,
		shouldUnregister: true
	});

	const today = new Date();

	const dateOptions = useMemo(
		() => formatDatepickerOptions(reservationWindow, closures, today),
		[reservationWindow, closures, today]
	);

	const haveValuesChanged = () => {
		const currentValues = getValues();
		return Object.keys(currentValues).some(
			(key) => currentValues[key] !== lastSubmitValues[key]
		);
	};

	const getLocalNumber = async () => {
		const [, , country, city] = cityUrl.split("/");
		try {
			const response = await handleFetchNumber(city, country);
			setPhone(response);
		} catch (error) {
			setPhone({
				number: "800555555",
				formattedNumber: "(800) 555 - 5555"
			});
			traceError("UnitReservationForm: Error fetching phone number", error);
		}
	};

	const handleSubmit = (data, event) => {
		event.preventDefault();

		if (isSubmitted && !haveValuesChanged()) {
			getLocalNumber();
			setIsSubmitWithoutChanges(true);
			setErrorReservationMessages("ReSubmissionError");
			return;
		}

		setLastSubmitValues(getValues());
		setIsSubmitWithoutChanges(false);

		const clickUrl = data?.unit?.url || null;

		trackCTAClick(
			"complete-reservation",
			"Reserve Now Button Clicked",
			"Reserve Now",
			clickUrl
		);

		onSubmit(data);
	};

	useEffect(() => {
		if (isSubmitted) {
			// Watch for changes in form fields
			const subscription = watch(() => {
				if (wasSubmittedWithoutChanges) {
					setIsSubmitWithoutChanges(false);
					setErrorReservationMessages();
				}
			});
			// clean up subscription on unmount
			return () => subscription.unsubscribe();
		}
		return () => {};
	}, [watch, wasSubmittedWithoutChanges, isSubmitted]);

	return (
		<div className="unit-reservation-form">
			<H4>Please Enter Your Contact Information to Reserve This Unit</H4>
			<Form
				methods={useFormMethods}
				onSubmit={handleSubmit}
				formName="unit-reservation-form"
			>
				<div className="main-inputs">
					<TextField {...UNIT_FORM_INPUTS.firstName} />
					<TextField {...UNIT_FORM_INPUTS.lastName} />
					<EmailField {...UNIT_FORM_INPUTS.email} />
					<PhoneField {...UNIT_FORM_INPUTS.phoneNumber} />
					<SelectField
						{...UNIT_FORM_INPUTS.moveInDate}
						{...dateOptions}
						icon={<Calendar />}
					/>
				</div>
				<AdditionalNames
					handleAddName={() => {
						append({ firstName: "", lastName: "" });
					}}
				>
					{fields.map((field, index) => (
						<AdditionalNameInputsGroup
							key={field.id}
							handleRemoveName={() => remove(index)}
						>
							<TextField
								{...ADDITIONAL_NAME_INPUTS.additionalFirstName(
									index
								)}
							/>
							<TextField
								{...ADDITIONAL_NAME_INPUTS.additionalLastName(
									index
								)}
							/>
						</AdditionalNameInputsGroup>
					))}
				</AdditionalNames>
				{showFeeWarning && totalFees > 0 && (
					<FeeAlert
						fees={fees}
						totalFees={totalFees}
						unitPrice={unitPrice}
					/>
				)}
				{wasSubmittedWithoutChanges && (
					<div className="main-alert-container">
						<WarningIcon size="xlarge" />
						<div>
							<Paragraph
								weight="extraBold"
								last
							>
								{"Your reservation had an error. "}
							</Paragraph>
							<Paragraph last>
								Please check the information you provided and
								make a change to resubmit your reservation.
								{errorReservationMessages !==
									"ReSubmissionError" && (
									<>
										<br /> &nbsp;&#8226;{" "}
										{errorReservationMessages}{" "}
									</>
								)}
							</Paragraph>
						</div>
					</div>
				)}
				<Button
					type="submit"
					theme="primary"
					disabled={wasSubmittedWithoutChanges}
				>
					Reserve Now
				</Button>
				{wasSubmittedWithoutChanges && (
					<div className="main-alert-container alert-with-suggestion">
						<Paragraph last>
							If you continue to experience errors with this
							reservation, you may need to come back and try again
							at a later time or &nbsp;
							<Link
								href={cityUrl}
								rel="noopener noreferrer"
							>
								try exploring other storage options in the same
								area if you are looking to reserve storage right
								now
							</Link>{" "}
							<span>
								or call{" "}
								<a href={`tel:${phone?.number}`}>
									{phone?.formattedNumber}
								</a>{" "}
								to get help in finding the best storage option
								for you.
							</span>
						</Paragraph>
					</div>
				)}
			</Form>
		</div>
	);
};

UnitReservationForm.propTypes = {
	onSubmit: PropTypes.func.isRequired,
	reservationWindow: PropTypes.number.isRequired,
	closures: PropTypes.arrayOf(PropTypes.string),
	showFeeWarning: PropTypes.bool,
	fees: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			price: PropTypes.number
		})
	),
	totalFees: PropTypes.number,
	unitPrice: PropTypes.number,
	cityUrl: PropTypes.string,
	errorReservationMessages: PropTypes.string,
	setErrorReservationMessages: PropTypes.func,
	handleFetchNumber: PropTypes.func
};

export default UnitReservationForm;
